import { useEffect, useMemo, useRef, useState } from "react";
import { atom } from "@tldraw/state";
import type { TLShape, TLShapeId } from "@tldraw/tlschema";
import {
  BaseBoxShapeUtil,
  HTMLContainer,
  resizeBox,
  useEditor,
  useIsEditing,
  type Editor,
  type ResizeInfo,
} from "./canvas";
import { sceneChange, takeFreshScene } from "./canvas/engine/shape-entrance";
import { sceneProps, type SceneShapeProps } from "../shared/schema";
import { readSceneSpec, type SceneSpec } from "../shared/scene-spec";
import { SceneThumbnail } from "./AgentResources";
import type { ScenePick, SceneView } from "./scene-engine";

/**
 * A 3D model on the board. It shows a still render, so a board of models
 * stays light; it plays its build once when it arrives; and double-clicking
 * it orbits in place. Leaving orbit saves the camera, so the still and any
 * export show the view that was framed.
 */
export type SceneShape = TLShape<"scene">;

const reducedMotion = () =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

type Live = {
  setView: (view: SceneView) => void;
  replay: () => void;
  getCamera: () => number[];
  setInteractive: (on: boolean) => void;
  dispose: () => void;
};
/** Models currently mounted live (orbiting or building), for the toolbar. */
export const liveScenes = new Map<string, Live>();
/** The element picked while orbiting, shown by the scene toolbar. */
export const scenePick = atom<{ shapeId: string; pick: ScenePick } | null>("scene pick", null);

// ----- still renders, shared and cached -----

const stills = new Map<string, Promise<string>>();
function hash(text: string) {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
/** A still of the model as the board shows it; cached by content, view and size. */
export function sceneStill(props: SceneShapeProps): Promise<string> {
  const key = [
    hash(props.spec),
    props.view,
    props.camera.join(","),
    Math.round(props.w / 40),
    Math.round(props.h / 40),
  ].join("|");
  let found = stills.get(key);
  if (found) {
    stills.delete(key);
    stills.set(key, found);
    return found;
  }
  found = (async () => {
    const spec = readSceneSpec(props.spec);
    if (!spec) throw new Error("This 3D model cannot be shown.");
    const { renderStill } = await import("./scene-engine");
    return renderStill(spec, {
      width: props.w,
      height: props.h,
      view: props.view === "custom" ? undefined : props.view,
      camera: props.view === "custom" ? props.camera : undefined,
    });
  })();
  found.catch(() => stills.delete(key));
  stills.set(key, found);
  while (stills.size > 40) stills.delete(stills.keys().next().value!);
  return found;
}

// ----- events the workspace answers -----

export type SceneRequest =
  | { action: "open"; shapeId: string }
  | { action: "comment"; shapeId: string; pick: ScenePick };
export function requestScene(detail: SceneRequest) {
  window.dispatchEvent(new CustomEvent<SceneRequest>("calque:scene", { detail }));
}

/**
 * Open a comment on the drawing at the point picked in 3D: on the plan object
 * the element came from, or on its sheet.
 */
export function commentOnPlan(
  editor: Editor,
  spec: SceneSpec,
  pick: ScenePick,
  commentAt: (shape: TLShape, point: { x: number; y: number }) => void,
) {
  if (!spec.plan) return "This model was not built from a sheet.";
  const sheet = editor.getShape(spec.plan.sheetId as TLShapeId);
  if (!sheet) return "The plan sheet is no longer on the canvas.";
  const source = pick.element.sourceId
    ? editor.getShape(pick.element.sourceId as TLShapeId)
    : undefined;
  const target = source ?? sheet;
  const mpu = spec.plan.metresPerUnit;
  const page = editor
    .getShapePageTransform(sheet.id)
    .applyToPoint({ x: pick.plan.x / mpu, y: pick.plan.y / mpu });
  const local = editor.getPointInShapeSpace(target, page);
  const b = editor.getShapeGeometry(target).bounds;
  const point = {
    x: Math.min(b.maxX, Math.max(b.minX, local.x)),
    y: Math.min(b.maxY, Math.max(b.minY, local.y)),
  };
  editor.setEditingShape(null);
  editor.setCurrentPage(editor.getAncestorPageId(target)!);
  editor.select(target.id);
  editor.centerOnPoint(page, { animation: { duration: 600 } });
  commentAt(target, point);
  return null;
}

export async function downloadScene(spec: SceneSpec) {
  const { sceneToGlb } = await import("./scene-engine");
  const file = await sceneToGlb(spec);
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ----- the shape -----

function SceneTile({ shape }: { shape: SceneShape }) {
  const editor = useEditor();
  const editing = useIsEditing(shape.id);
  const spec = useMemo(() => readSceneSpec(shape.props.spec), [shape.props.spec]);
  const [building, setBuilding] = useState(() => takeFreshScene(shape.id) && !reducedMotion());
  // An agent edit arrived: rebuild just what changed, live, then return to the still.
  const [changing, setChanging] = useState(false);
  const [still, setStill] = useState<string | null>(null);
  const live = editing || building || changing;
  const host = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const controller = useRef<Live | null>(null);
  // Updated in an effect, not during render: when orbit ends, the live view's
  // cleanup must still see that it was orbiting so it can save the camera.
  const interactive = useRef(editing);
  const props = shape.props;

  // The still, re-rendered (debounced) when the model, view or size changes.
  useEffect(() => {
    if (!spec) return;
    let cancelled = false;
    const timer = setTimeout(
      () =>
        sceneStill(props)
          .then((url) => !cancelled && setStill(url))
          .catch(() => !cancelled && setStill(null)),
      still ? 250 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, props.view, props.camera.join(","), Math.round(props.w / 40), Math.round(props.h / 40)]);

  useEffect(() => {
    if (sceneChange(shape.id) && !reducedMotion()) setChanging(true);
  }, [spec, shape.id]);

  // Live WebGL only while building, rebuilding an edit or orbiting.
  useEffect(() => {
    if (!live || !spec || !host.current) return;
    let disposed = false;
    const changed = building ? undefined : sceneChange(shape.id, true);
    void import("./scene-engine").then(({ mountScene }) => {
      if (disposed || !host.current) return;
      controller.current = mountScene(host.current, spec, {
        view: props.view === "custom" ? undefined : props.view,
        camera: props.view === "custom" ? props.camera : undefined,
        build: building || !!changed,
        changed,
        interactive: interactive.current,
        reducedMotion: reducedMotion(),
        onBuilt: () => {
          setBuilding(false);
          setChanging(false);
        },
        onPick: (pick) => scenePick.set(pick ? { shapeId: shape.id, pick } : null),
      });
      liveScenes.set(shape.id, controller.current);
      // Nothing left to rebuild (the change went stale): back to the still.
      if (!building && !changed) setChanging(false);
    });
    return () => {
      disposed = true;
      const live = controller.current;
      controller.current = null;
      liveScenes.delete(shape.id);
      if (!live) return;
      // Leaving orbit keeps the framed view.
      if (interactive.current) {
        const camera = live.getCamera();
        const current = editor.getShape(shape.id) as SceneShape | undefined;
        if (current && camera.join(",") !== current.props.camera.join(","))
          editor.updateShape({ id: shape.id, type: "scene", props: { view: "custom", camera } });
      }
      live.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, spec]);

  useEffect(() => {
    interactive.current = editing;
    controller.current?.setInteractive(editing);
    if (editing) root.current?.focus({ preventScroll: true });
    else if (scenePick.get()?.shapeId === shape.id) scenePick.set(null);
  }, [editing, shape.id]);

  // While orbiting, the wheel zooms the model, not the board.
  useEffect(() => {
    const el = root.current;
    if (!el || !editing) return;
    const stop = (e: WheelEvent) => e.stopPropagation();
    el.addEventListener("wheel", stop, { passive: true });
    return () => el.removeEventListener("wheel", stop);
  }, [editing]);

  return (
    <div
      ref={root}
      className={`scene-tile ${editing ? "orbiting" : ""}`}
      // While orbiting, drags belong to the model, not the board.
      data-canvas-ignore={editing ? "" : undefined}
      tabIndex={editing ? 0 : -1}
      style={{ width: props.w, height: props.h, pointerEvents: editing ? "all" : undefined }}
      onPointerDown={editing ? (e) => e.stopPropagation() : undefined}
      onKeyDown={
        editing
          ? (e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                editor.setEditingShape(null);
                editor.setCurrentTool("select");
              }
            }
          : undefined
      }
    >
      {still ? (
        <img className="scene-tile-still" src={still} alt={props.name} draggable={false} />
      ) : spec ? (
        <div className="scene-tile-sketch">
          <SceneThumbnail scene={spec} />
        </div>
      ) : (
        <div className="scene-tile-sketch">This 3D model cannot be shown.</div>
      )}
      {live && <div ref={host} className="scene-tile-live" />}
      <span className="scene-tile-badge">{editing ? "Drag to orbit · Esc when done" : "3D"}</span>
      {spec?.approximate && !editing && (
        <span className="scene-tile-approximate" title="Walls and openings were traced from the drawing image; check dimensions before relying on them.">
          Traced from drawing · approximate
        </span>
      )}
    </div>
  );
}

export class SceneUtil extends BaseBoxShapeUtil<SceneShape> {
  static override type = "scene" as const;
  static override props = sceneProps;
  getDefaultProps(): SceneShapeProps {
    return { w: 800, h: 600, name: "3D model", spec: "{}", view: "iso", camera: [] };
  }
  override canEdit() {
    return true;
  }
  override getIndicatorPath(shape: SceneShape) {
    const path = new Path2D();
    path.rect(0, 0, shape.props.w, shape.props.h);
    return path;
  }
  component(shape: SceneShape) {
    return (
      <HTMLContainer>
        <SceneTile shape={shape} />
      </HTMLContainer>
    );
  }
  override onResize(shape: SceneShape, info: ResizeInfo<SceneShape>) {
    return resizeBox(shape, info, { minWidth: 200, maxWidth: 10000, minHeight: 150, maxHeight: 10000 });
  }
  override async toSvg(shape: SceneShape) {
    const { w, h } = shape.props;
    try {
      const url = await sceneStill(shape.props);
      return <image href={url} width={w} height={h} preserveAspectRatio="xMidYMid slice" />;
    } catch {
      return (
        <g>
          <rect width={w} height={h} fill="#f3f2ee" stroke="#d6d4ce" />
          <text x={16} y={32} fontSize={18} fill="#5d655f">
            {shape.props.name}
          </text>
        </g>
      );
    }
  }
}
